import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { 
  FileText, 
  Search, 
  Calendar,
  Clock,
  Euro,
  CheckCircle2,
  Download,
  Edit3,
  Eye,
  Loader2,
  FolderKanban,
  FileSpreadsheet,
  ClipboardList,
  History,
  Printer,
  Save,
  X,
  ChevronRight,
  Building,
  User,
  TrendingUp,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const ReportHistoryPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('projects');
  
  // Data
  const [projectReports, setProjectReports] = useState([]);
  const [contractReports, setContractReports] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // View/Edit dialog
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedNotes, setEditedNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const [projectRes, contractRes] = await Promise.all([
        api.get('/reports/project-history'),
        api.get('/reports/contract-history')
      ]);
      setProjectReports(projectRes.data || []);
      setContractReports(contractRes.data || []);
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Erreur lors du chargement des rapports');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const openReportDetail = (report, type) => {
    setSelectedReport({ ...report, type });
    setEditedNotes(report.agency_notes || report.notes || '');
    setIsEditing(false);
    setShowDetailDialog(true);
  };

  const handleSaveNotes = async () => {
    if (!selectedReport) return;
    setSaving(true);
    try {
      const endpoint = selectedReport.type === 'project' 
        ? `/projects/${selectedReport.project_id}/report`
        : `/contracts/${selectedReport.contract_id}/report/${selectedReport.id}`;
      
      await api.put(endpoint, { 
        notes: editedNotes,
        agency_notes: editedNotes 
      });
      
      toast.success('Notes enregistrées');
      setIsEditing(false);
      fetchReports();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const exportToPDF = (report) => {
    const doc = new jsPDF();
    const isProject = report.type === 'project';
    
    // Title
    doc.setFontSize(20);
    doc.setTextColor(33, 33, 33);
    doc.text(isProject ? 'Rapport de Projet' : 'Rapport de Maintenance', 20, 20);
    
    // Subtitle
    doc.setFontSize(14);
    doc.setTextColor(100, 100, 100);
    doc.text(report.project_name || report.contract_name || 'Rapport', 20, 30);
    
    // Date
    doc.setFontSize(10);
    doc.text(`Généré le ${formatDate(report.generated_at)}`, 20, 38);
    
    // Separator line
    doc.setDrawColor(200, 200, 200);
    doc.line(20, 42, 190, 42);
    
    // Content based on report type
    let y = 50;
    
    if (isProject) {
      // Project details
      doc.setFontSize(12);
      doc.setTextColor(33, 33, 33);
      doc.text('Détails du projet', 20, y);
      y += 8;
      
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      
      const projectDetails = [
        ['Client', report.client_name || '-'],
        ['Statut', report.project_status || '-'],
        ['Budget', formatCurrency(report.finances?.budget)],
        ['Dépenses', formatCurrency(report.finances?.expenses)],
        ['Marge', `${report.finances?.margin_percentage || 0}%`],
        ['Heures estimées', `${report.total_hours_estimated || 0}h`],
        ['Heures réelles', `${report.total_hours_actual || 0}h`]
      ];
      
      doc.autoTable({
        startY: y,
        head: [],
        body: projectDetails,
        theme: 'plain',
        styles: { fontSize: 10 },
        columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50 } }
      });
      
      y = doc.lastAutoTable.finalY + 10;
      
      // Tasks summary
      if (report.tasks && report.tasks.length > 0) {
        doc.setFontSize(12);
        doc.setTextColor(33, 33, 33);
        doc.text('Tâches', 20, y);
        y += 5;
        
        const taskData = report.tasks.map(t => [
          t.name || t.title || '-',
          t.status || '-',
          `${t.hours_actual || 0}h`
        ]);
        
        doc.autoTable({
          startY: y,
          head: [['Tâche', 'Statut', 'Heures']],
          body: taskData,
          theme: 'striped',
          styles: { fontSize: 9 },
          headStyles: { fillColor: [100, 100, 100] }
        });
      }
    } else {
      // Contract details
      doc.setFontSize(12);
      doc.setTextColor(33, 33, 33);
      doc.text('Détails du contrat', 20, y);
      y += 8;
      
      doc.setFontSize(10);
      const contractDetails = [
        ['Période', `${report.month || ''} ${report.year || ''}`],
        ['Heures incluses', `${report.hours_included || 0}h`],
        ['Heures utilisées', `${report.total_hours || 0}h`],
        ['Montant', formatCurrency(report.total_amount)]
      ];
      
      doc.autoTable({
        startY: y,
        head: [],
        body: contractDetails,
        theme: 'plain',
        styles: { fontSize: 10 },
        columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50 } }
      });
      
      y = doc.lastAutoTable.finalY + 10;
      
      // Tasks
      if (report.tasks && report.tasks.length > 0) {
        doc.setFontSize(12);
        doc.text('Interventions', 20, y);
        y += 5;
        
        const taskData = report.tasks.map(t => [
          t.name || t.description || '-',
          `${t.hours || t.duration_hours || 0}h`,
          formatCurrency(t.amount)
        ]);
        
        doc.autoTable({
          startY: y,
          head: [['Description', 'Heures', 'Montant']],
          body: taskData,
          theme: 'striped',
          styles: { fontSize: 9 },
          headStyles: { fillColor: [100, 100, 100] }
        });
      }
    }
    
    // Notes
    if (report.agency_notes || report.notes) {
      y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 15 : y + 10;
      doc.setFontSize(12);
      doc.setTextColor(33, 33, 33);
      doc.text('Notes', 20, y);
      y += 8;
      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      const notes = doc.splitTextToSize(report.agency_notes || report.notes, 170);
      doc.text(notes, 20, y);
    }
    
    // Save
    const filename = isProject 
      ? `rapport_projet_${report.project_name?.replace(/\s+/g, '_') || 'projet'}.pdf`
      : `rapport_maintenance_${report.month || ''}_${report.year || ''}.pdf`;
    doc.save(filename);
  };

  const exportToCSV = (report) => {
    const isProject = report.type === 'project';
    let csvContent = '';
    
    if (isProject) {
      csvContent = 'Rapport de Projet\n\n';
      csvContent += `Projet,${report.project_name || ''}\n`;
      csvContent += `Client,${report.client_name || ''}\n`;
      csvContent += `Date,${formatDate(report.generated_at)}\n`;
      csvContent += `Budget,${report.finances?.budget || 0}\n`;
      csvContent += `Dépenses,${report.finances?.expenses || 0}\n`;
      csvContent += `Marge,${report.finances?.margin_percentage || 0}%\n\n`;
      
      if (report.tasks && report.tasks.length > 0) {
        csvContent += 'Tâches\n';
        csvContent += 'Nom,Statut,Heures\n';
        report.tasks.forEach(t => {
          csvContent += `"${t.name || t.title || ''}","${t.status || ''}",${t.hours_actual || 0}\n`;
        });
      }
    } else {
      csvContent = 'Rapport de Maintenance\n\n';
      csvContent += `Contrat,${report.contract_name || ''}\n`;
      csvContent += `Période,${report.month || ''} ${report.year || ''}\n`;
      csvContent += `Heures incluses,${report.hours_included || 0}\n`;
      csvContent += `Heures utilisées,${report.total_hours || 0}\n`;
      csvContent += `Montant,${report.total_amount || 0}\n\n`;
      
      if (report.tasks && report.tasks.length > 0) {
        csvContent += 'Interventions\n';
        csvContent += 'Description,Heures,Montant\n';
        report.tasks.forEach(t => {
          csvContent += `"${t.name || t.description || ''}",${t.hours || t.duration_hours || 0},${t.amount || 0}\n`;
        });
      }
    }
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = isProject 
      ? `rapport_projet_${report.project_name?.replace(/\s+/g, '_') || 'projet'}.csv`
      : `rapport_maintenance_${report.month || ''}_${report.year || ''}.csv`;
    link.click();
  };

  // Filter reports based on search
  const filteredProjectReports = projectReports.filter(r => 
    (r.project_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (r.client_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredContractReports = contractReports.filter(r =>
    (r.contract_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (r.client_name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 px-4 md:px-6 pb-6",
        collapsed ? "md:ml-20" : "md:ml-64"
      )} style={{ paddingTop: "130px" }}>
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: theme.colors?.primary }}>
              <History className="w-6 h-6" />
              Historique des Rapports
            </h1>
            <p className="text-slate-500 text-sm">Consultez, éditez et exportez vos rapports générés</p>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-64"
              />
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <FolderKanban className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-700">{projectReports.length}</p>
                  <p className="text-xs text-blue-600">Rapports projet</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <ClipboardList className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-700">{contractReports.length}</p>
                  <p className="text-xs text-emerald-600">Rapports maintenance</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-700">{projectReports.length + contractReports.length}</p>
                  <p className="text-xs text-purple-600">Total rapports</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-700">
                    {new Date().toLocaleDateString('fr-FR', { month: 'short' })}
                  </p>
                  <p className="text-xs text-amber-600">Mois en cours</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="projects" className="gap-2">
              <FolderKanban className="w-4 h-4" />
              Projets ({filteredProjectReports.length})
            </TabsTrigger>
            <TabsTrigger value="contracts" className="gap-2">
              <ClipboardList className="w-4 h-4" />
              Maintenance ({filteredContractReports.length})
            </TabsTrigger>
          </TabsList>

          {/* Project Reports Tab */}
          <TabsContent value="projects">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : filteredProjectReports.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FolderKanban className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">Aucun rapport de projet</p>
                  <p className="text-sm text-slate-400 mt-1">
                    Les rapports sont générés automatiquement à la fin des projets
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {filteredProjectReports.map((report) => (
                  <Card key={report.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                            <FolderKanban className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-800">{report.project_name}</h3>
                            <p className="text-sm text-slate-500 flex items-center gap-2">
                              <Building className="w-3 h-3" />
                              {report.client_name || 'Client'}
                            </p>
                            <div className="flex items-center gap-3 mt-2">
                              <Badge variant="outline" className="text-xs">
                                <Calendar className="w-3 h-3 mr-1" />
                                {formatDate(report.generated_at)}
                              </Badge>
                              {report.finances?.margin_percentage && (
                                <Badge 
                                  className={cn(
                                    "text-xs",
                                    report.finances.margin_percentage > 30 
                                      ? "bg-green-100 text-green-700"
                                      : report.finances.margin_percentage > 15
                                      ? "bg-amber-100 text-amber-700"
                                      : "bg-red-100 text-red-700"
                                  )}
                                >
                                  <TrendingUp className="w-3 h-3 mr-1" />
                                  Marge: {report.finances.margin_percentage}%
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openReportDetail(report, 'project')}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Voir
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => exportToPDF({ ...report, type: 'project' })}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            PDF
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => exportToCSV({ ...report, type: 'project' })}
                          >
                            <FileSpreadsheet className="w-4 h-4 mr-1" />
                            CSV
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Contract Reports Tab */}
          <TabsContent value="contracts">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : filteredContractReports.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <ClipboardList className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">Aucun rapport de maintenance</p>
                  <p className="text-sm text-slate-400 mt-1">
                    Les rapports sont générés automatiquement à la fin de chaque mois
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {filteredContractReports.map((report) => (
                  <Card key={report.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                            <ClipboardList className="w-6 h-6 text-emerald-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-800">
                              {report.contract_name || `Contrat ${report.contract_id?.slice(0, 8)}...`}
                            </h3>
                            <p className="text-sm text-slate-500">
                              {report.month} {report.year}
                            </p>
                            <div className="flex items-center gap-3 mt-2">
                              <Badge variant="outline" className="text-xs">
                                <Clock className="w-3 h-3 mr-1" />
                                {report.total_hours || 0}h / {report.hours_included || 0}h
                              </Badge>
                              <Badge 
                                className={cn(
                                  "text-xs",
                                  (report.total_hours || 0) <= (report.hours_included || 0)
                                    ? "bg-green-100 text-green-700"
                                    : "bg-red-100 text-red-700"
                                )}
                              >
                                <Euro className="w-3 h-3 mr-1" />
                                {formatCurrency(report.total_amount)}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openReportDetail(report, 'contract')}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Voir
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => exportToPDF({ ...report, type: 'contract' })}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            PDF
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => exportToCSV({ ...report, type: 'contract' })}
                          >
                            <FileSpreadsheet className="w-4 h-4 mr-1" />
                            CSV
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Report Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{ color: theme.colors?.primary }}>
                {selectedReport?.type === 'project' ? (
                  <FolderKanban className="w-5 h-5" />
                ) : (
                  <ClipboardList className="w-5 h-5" />
                )}
                {selectedReport?.type === 'project' ? 'Rapport de Projet' : 'Rapport de Maintenance'}
              </DialogTitle>
            </DialogHeader>

            {selectedReport && (
              <div className="space-y-6">
                {/* Header info */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h3 className="font-semibold text-lg">
                    {selectedReport.project_name || selectedReport.contract_name}
                  </h3>
                  <p className="text-sm text-slate-500">
                    Généré le {formatDate(selectedReport.generated_at)}
                  </p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {selectedReport.type === 'project' ? (
                    <>
                      <div className="p-3 bg-blue-50 rounded-lg text-center">
                        <p className="text-lg font-bold text-blue-700">
                          {formatCurrency(selectedReport.finances?.budget)}
                        </p>
                        <p className="text-xs text-blue-600">Budget</p>
                      </div>
                      <div className="p-3 bg-amber-50 rounded-lg text-center">
                        <p className="text-lg font-bold text-amber-700">
                          {formatCurrency(selectedReport.finances?.expenses)}
                        </p>
                        <p className="text-xs text-amber-600">Dépenses</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg text-center">
                        <p className="text-lg font-bold text-green-700">
                          {selectedReport.finances?.margin_percentage || 0}%
                        </p>
                        <p className="text-xs text-green-600">Marge</p>
                      </div>
                      <div className="p-3 bg-purple-50 rounded-lg text-center">
                        <p className="text-lg font-bold text-purple-700">
                          {selectedReport.tasks?.length || 0}
                        </p>
                        <p className="text-xs text-purple-600">Tâches</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="p-3 bg-blue-50 rounded-lg text-center">
                        <p className="text-lg font-bold text-blue-700">
                          {selectedReport.hours_included || 0}h
                        </p>
                        <p className="text-xs text-blue-600">Heures incluses</p>
                      </div>
                      <div className="p-3 bg-amber-50 rounded-lg text-center">
                        <p className="text-lg font-bold text-amber-700">
                          {selectedReport.total_hours || 0}h
                        </p>
                        <p className="text-xs text-amber-600">Heures utilisées</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg text-center">
                        <p className="text-lg font-bold text-green-700">
                          {formatCurrency(selectedReport.total_amount)}
                        </p>
                        <p className="text-xs text-green-600">Montant</p>
                      </div>
                      <div className="p-3 bg-purple-50 rounded-lg text-center">
                        <p className="text-lg font-bold text-purple-700">
                          {selectedReport.tasks?.length || 0}
                        </p>
                        <p className="text-xs text-purple-600">Interventions</p>
                      </div>
                    </>
                  )}
                </div>

                {/* Tasks/Interventions - Detailed view */}
                {selectedReport.tasks && selectedReport.tasks.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      {selectedReport.type === 'project' ? 'Tâches détaillées' : 'Interventions détaillées'}
                      <Badge variant="outline">{selectedReport.tasks.length}</Badge>
                    </h4>
                    <div className="space-y-3 max-h-[400px] overflow-y-auto border rounded-lg p-3 bg-slate-50">
                      {selectedReport.tasks.map((task, idx) => (
                        <div key={idx} className="p-4 bg-white border rounded-lg shadow-sm">
                          {/* Task header */}
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <p className="font-semibold text-slate-800">{task.name || task.title || task.description}</p>
                              <div className="flex flex-wrap items-center gap-2 mt-1">
                                <Badge 
                                  variant="outline" 
                                  className={cn(
                                    "text-xs",
                                    task.status === 'completed' ? 'bg-green-50 text-green-700 border-green-200' :
                                    task.status === 'validated' ? 'bg-cyan-50 text-cyan-700 border-cyan-200' :
                                    task.status === 'in_progress' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                    'bg-slate-50 text-slate-600'
                                  )}
                                >
                                  {task.status === 'completed' ? '✓ Terminé' :
                                   task.status === 'validated' ? '✓ Validé' :
                                   task.status === 'in_progress' ? '⏳ En cours' :
                                   task.status || '-'}
                                </Badge>
                                {task.priority && (
                                  <Badge 
                                    variant="outline"
                                    className={cn(
                                      "text-xs",
                                      task.priority === 'high' ? 'bg-red-50 text-red-700' :
                                      task.priority === 'medium' ? 'bg-amber-50 text-amber-700' :
                                      'bg-slate-50 text-slate-600'
                                    )}
                                  >
                                    {task.priority === 'high' ? '🔴 Haute' :
                                     task.priority === 'medium' ? '🟠 Moyenne' : '⚪ Normale'}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-mono text-lg font-bold text-slate-800">
                                {task.hours_actual || task.hours || task.duration_hours || 0}h
                              </p>
                              {(task.estimated_hours || task.hours_estimated) && (
                                <p className="text-xs text-slate-500">
                                  / {task.estimated_hours || task.hours_estimated}h estimé
                                </p>
                              )}
                            </div>
                          </div>
                          
                          {/* Task details */}
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            {/* Assignee */}
                            {(task.assignee_name || task.assigned_to_name) && (
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-slate-400" />
                                <span className="text-slate-600">
                                  <span className="font-medium">Réalisé par:</span> {task.assignee_name || task.assigned_to_name}
                                </span>
                              </div>
                            )}
                            
                            {/* Date */}
                            {(task.completed_at || task.date || task.created_at) && (
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-slate-400" />
                                <span className="text-slate-600">
                                  {formatDate(task.completed_at || task.date || task.created_at)}
                                </span>
                              </div>
                            )}
                            
                            {/* Amount */}
                            {task.amount && (
                              <div className="flex items-center gap-2">
                                <Euro className="w-4 h-4 text-slate-400" />
                                <span className="text-slate-600 font-medium">
                                  {formatCurrency(task.amount)}
                                </span>
                              </div>
                            )}
                            
                            {/* Hourly rate */}
                            {task.hourly_rate && (
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-slate-400" />
                                <span className="text-slate-600">
                                  {formatCurrency(task.hourly_rate)}/h
                                </span>
                              </div>
                            )}
                          </div>
                          
                          {/* Time entries if available */}
                          {task.time_entries && task.time_entries.length > 0 && (
                            <div className="mt-3 pt-3 border-t">
                              <p className="text-xs font-medium text-slate-500 mb-2">Temps passé détaillé:</p>
                              <div className="space-y-1">
                                {task.time_entries.map((entry, eIdx) => (
                                  <div key={eIdx} className="flex items-center justify-between text-xs bg-slate-50 p-2 rounded">
                                    <div className="flex items-center gap-2">
                                      <User className="w-3 h-3 text-slate-400" />
                                      <span>{entry.user_name || entry.member_name || 'Membre'}</span>
                                      {entry.date && (
                                        <span className="text-slate-400">- {formatDate(entry.date)}</span>
                                      )}
                                    </div>
                                    <span className="font-mono font-medium">{entry.hours || entry.duration || 0}h</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Files/Proofs if available */}
                          {task.files && task.files.length > 0 && (
                            <div className="mt-3 pt-3 border-t">
                              <p className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1">
                                <FileText className="w-3 h-3" /> Preuves / Fichiers joints:
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {task.files.map((file, fIdx) => (
                                  <a 
                                    key={fIdx}
                                    href={file.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded hover:bg-blue-100"
                                  >
                                    <Download className="w-3 h-3" />
                                    {file.name || `Fichier ${fIdx + 1}`}
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Validation info */}
                          {(task.agency_validated || task.client_validated) && (
                            <div className="mt-3 pt-3 border-t flex flex-wrap gap-2">
                              {task.agency_validated && (
                                <Badge className="bg-cyan-100 text-cyan-700 text-xs">
                                  ✓ Validé agence {task.agency_validated_at && `(${formatDate(task.agency_validated_at)})`}
                                </Badge>
                              )}
                              {task.client_validated && (
                                <Badge className="bg-green-100 text-green-700 text-xs">
                                  ✓ Validé client {task.client_validated_at && `(${formatDate(task.client_validated_at)})`}
                                </Badge>
                              )}
                            </div>
                          )}
                          
                          {/* Description if available */}
                          {task.description && task.description !== task.name && (
                            <div className="mt-3 pt-3 border-t">
                              <p className="text-xs text-slate-500">{task.description}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {/* Summary */}
                    <div className="mt-4 p-3 bg-slate-100 rounded-lg">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="text-lg font-bold text-slate-800">
                            {selectedReport.tasks.reduce((sum, t) => sum + (t.hours_actual || t.hours || t.duration_hours || 0), 0)}h
                          </p>
                          <p className="text-xs text-slate-500">Total heures</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold text-green-600">
                            {selectedReport.tasks.filter(t => t.status === 'completed' || t.status === 'validated').length}
                          </p>
                          <p className="text-xs text-slate-500">Terminées</p>
                        </div>
                        <div>
                          <p className="text-lg font-bold text-blue-600">
                            {formatCurrency(selectedReport.tasks.reduce((sum, t) => sum + (t.amount || 0), 0))}
                          </p>
                          <p className="text-xs text-slate-500">Total montant</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold">Notes</h4>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setIsEditing(!isEditing)}
                    >
                      {isEditing ? <X className="w-4 h-4 mr-1" /> : <Edit3 className="w-4 h-4 mr-1" />}
                      {isEditing ? 'Annuler' : 'Modifier'}
                    </Button>
                  </div>
                  
                  {isEditing ? (
                    <div className="space-y-3">
                      <Textarea
                        value={editedNotes}
                        onChange={(e) => setEditedNotes(e.target.value)}
                        placeholder="Ajoutez des notes au rapport..."
                        rows={4}
                      />
                      <Button 
                        onClick={handleSaveNotes}
                        disabled={saving}
                        className="w-full"
                        style={{ backgroundColor: theme.colors?.accent }}
                      >
                        {saving ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4 mr-2" />
                        )}
                        Enregistrer
                      </Button>
                    </div>
                  ) : (
                    <div className="p-3 bg-slate-50 rounded-lg min-h-[80px]">
                      {selectedReport.agency_notes || selectedReport.notes || (
                        <span className="text-slate-400 italic">Aucune note</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button 
                variant="outline"
                onClick={() => selectedReport && exportToPDF(selectedReport)}
              >
                <Download className="w-4 h-4 mr-2" />
                Télécharger PDF
              </Button>
              <Button 
                variant="outline"
                onClick={() => selectedReport && exportToCSV(selectedReport)}
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Exporter CSV
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default ReportHistoryPage;
